/**
 * Node display-ordering — the single source for how catalog options sort.
 *
 * The wire delivers `nodes` keyed by id (a JSON object), so the client iterates
 * them in numeric-id order, NOT a meaningful display order. This module turns a
 * node's display `name` into a normalized SORT KEY and compares those keys
 * naturally, so columns read the way a human expects ("60" before "Serie 100",
 * "Rider …" next to "R …"). The displayed name is never altered — only the key.
 *
 * Used by `TreeService.childrenOf` (the one place children are derived for the
 * view), so both finders (modal ButtonColumnView + homepage DropdownView) get
 * the same order. Kept as a pure, framework-free pair (key + comparator) for
 * SRP — TreeService stays storage, this stays ordering policy.
 *
 * @see src-js/model-finder/data/tree-service.ts
 */

/** Bracketed suffix/segment, e.g. the trailing "[AWD]" variant marker. */
const BRACKETED = /\[[^\]]*\]/g;
/** Leading "Rider" as a whole word (case-insensitive). */
const LEADING_RIDER = /^rider\b/i;
/** Leading "Serie" as a whole word — `\b` stops "Seriennummer"/"Serien …" misfires. */
const LEADING_SERIE = /^serie\b/i;
/** Any run of whitespace, for collapsing. */
const WHITESPACE = /\s+/g;

/**
 * Explicit per-name sort-key overrides — deliberate, documented placements that
 * the generic normalization below cannot (and should not) derive from the name.
 *
 * `"61 - 66 Serie"` (Husqvarna chainsaws) is NOT a real "Serie N": it is a
 * legacy range label — an old data/display artifact (a mislabelled grouping that
 * upstream never cleaned up). Its generic key would be `"61 …"`, which natural
 * sort places at the TOP, BEFORE "Serie 100". The product owner instead wants
 * this legacy range listed at the END, AFTER the clean "Serie 100–500" hundreds.
 * We therefore map it to `"600"` so it sorts just past "Serie 500".
 *
 * This is a CONSCIOUS exception, NOT a sorting bug: the entry only looks
 * out of place because of the legacy naming. Keep this override until the
 * catalog data is renamed/cleaned upstream, then delete the row. The display
 * name is never touched — the list still reads "61 - 66 Serie".
 *
 * Keyed by the lower-cased, whitespace-collapsed display name (a Map, so a
 * user-supplied name can never collide with `Object.prototype` keys).
 */
const SORT_KEY_OVERRIDES = new Map<string, string>([
  ['61 - 66 serie', '600'],
]);

/**
 * The normalized sort key for a node's display name. An explicit override
 * (see {@link SORT_KEY_OVERRIDES}) wins; otherwise the generic key is built from
 * sequential steps, each here for a concrete catalog reason:
 *  1. Drop bracketed content — "R 322 T AWD [AWD]" must sort beside the
 *     bracketless "R 322 T AWD".
 *  2. Collapse + trim whitespace FIRST, so the "leading word" anchors (`^`) see
 *     the real first word even when the name had stray leading/inner spaces.
 *  3. Leading "Rider" → "R" — "Rider 322 T AWD" must group with "R 322 T AWD".
 *  4. Strip a LEADING "Serie" word — series should sort by their number, not by
 *     the constant "Serie" prefix. Only when leading: "Motorsägen Serien" keeps
 *     its "Serien" (it is trailing); `\b` also guards "Seriennummer".
 *  5. Final trim removes the space the "Serie" strip leaves behind.
 */
export function nodeSortKey(name: string): string {
  const override = SORT_KEY_OVERRIDES.get(name.replace(WHITESPACE, ' ').trim().toLowerCase());
  if (override !== undefined) {
    return override;
  }

  return name
    .replace(BRACKETED, ' ')
    .replace(WHITESPACE, ' ')
    .trim()
    .replace(LEADING_RIDER, 'R')
    .replace(LEADING_SERIE, '')
    .trim();
}

/**
 * Compare two nodes by their normalized sort key, NATURAL + case-insensitive.
 *
 * Natural (`numeric: true`) is the load-bearing choice: plain lexicographic
 * order puts "60" AFTER "300" ('6' > '3'), which is exactly the reported bug.
 * Numeric collation sorts 60 < 100 < 200 < 300 while the non-numeric residue
 * still compares as a normal string. `sensitivity: 'base'` makes it
 * case/diacritic-insensitive for stable grouping.
 */
export function compareNodes(a: { name: string }, b: { name: string }): number {
  return nodeSortKey(a.name).localeCompare(nodeSortKey(b.name), undefined, {
    numeric: true,
    sensitivity: 'base',
  });
}
